<template>
    <Drawer v-model:open="drawerState" @update:open="onOpenChange">
        <DrawerContent class="max-h-[60vh]">
            <div class="mx-auto w-full max-w-xl">
                <DrawerHeader>
                    <DrawerTitle
                        >Place Details for
                        {{ profilePlaceDetails?.name || 'Unnamed Place' }}</DrawerTitle
                    >
                    <DrawerDescription>All details based on google place data</DrawerDescription>
                </DrawerHeader>
                <div class="p-4 pb-0">
                    <div class="flex items-center justify-center space-x-2">
                        <!-- <div class="w-full space-y-3"> -->
                        <div
                            v-if="profilePlaceDetails"
                            class="space-y-4 rounded-2xl border bg-card p-4 shadow-sm overflow-y-auto max-h-[50vh]"
                        >
                            <div class="space-y-1">
                                <p class="text-xs uppercase text-muted-foreground">
                                    Selected Place
                                </p>
                                <p class="text-lg font-semibold">
                                    {{ profilePlaceDetails.name || 'Unnamed Place' }}
                                </p>
                                <p class="text-sm text-muted-foreground">
                                    {{
                                        profilePlaceDetails.formatted_address ||
                                        'No formatted address'
                                    }}
                                </p>
                            </div>

                            <div class="grid gap-3 rounded-xl border bg-muted/30 p-4 text-sm">
                                <div>
                                    <p class="text-xs font-semibold text-muted-foreground">
                                        Business Status
                                    </p>
                                    <p class="font-medium">
                                        {{ profilePlaceDetails.business_status || 'Unknown' }}
                                    </p>
                                </div>
                                <div>
                                    <p class="text-xs font-semibold text-muted-foreground">
                                        Google Place ID
                                    </p>
                                    <p class="font-mono text-xs">
                                        {{ profilePlaceDetails.place_id || 'N/A' }}
                                    </p>
                                </div>
                                <div>
                                    <p class="text-xs font-semibold text-muted-foreground">
                                        Google Maps
                                    </p>
                                    <a
                                        v-if="profilePlaceDetails.url"
                                        :href="profilePlaceDetails.url"
                                        target="_blank"
                                        rel="noopener"
                                        class="text-primary underline-offset-4 hover:underline"
                                    >
                                        View details
                                    </a>
                                    <span v-else>N/A</span>
                                </div>
                            </div>

                            <div class="rounded-xl border p-4">
                                <p class="text-xs uppercase text-muted-foreground">Coordinates</p>
                                <div class="mt-3 grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <p class="text-xs text-muted-foreground">Latitude</p>
                                        <p class="font-semibold">
                                            {{ profilePlaceDetails.geometry?.location?.lat ?? '—' }}
                                        </p>
                                    </div>
                                    <div>
                                        <p class="text-xs text-muted-foreground">Longitude</p>
                                        <p class="font-semibold">
                                            {{ profilePlaceDetails.geometry?.location?.lng ?? '—' }}
                                        </p>
                                    </div>
                                    <div>
                                        <p class="text-xs text-muted-foreground">Viewport NE</p>
                                        <p class="font-semibold">
                                            {{
                                                profilePlaceDetails.geometry?.viewport?.northeast
                                                    ?.lat ?? '—'
                                            }},
                                            {{
                                                profilePlaceDetails.geometry?.viewport?.northeast
                                                    ?.lng ?? '—'
                                            }}
                                        </p>
                                    </div>
                                    <div>
                                        <p class="text-xs text-muted-foreground">Viewport SW</p>
                                        <p class="font-semibold">
                                            {{
                                                profilePlaceDetails.geometry?.viewport?.southwest
                                                    ?.lat ?? '—'
                                            }},
                                            {{
                                                profilePlaceDetails.geometry?.viewport?.southwest
                                                    ?.lng ?? '—'
                                            }}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div class="rounded-xl border p-4">
                                <p class="text-xs uppercase text-muted-foreground">Types</p>
                                <div
                                    v-if="profilePlaceDetails.types?.length"
                                    class="mt-3 flex flex-wrap gap-2"
                                >
                                    <Badge
                                        v-for="type in profilePlaceDetails.types"
                                        :key="type"
                                        variant="secondary"
                                        class="text-xs font-medium capitalize"
                                    >
                                        {{ type }}
                                    </Badge>
                                </div>
                                <p v-else class="mt-3 text-sm text-muted-foreground">
                                    No types available.
                                </p>
                            </div>

                            <div class="rounded-xl border p-4">
                                <p class="text-xs uppercase text-muted-foreground">
                                    Address Components
                                </p>
                                <div
                                    v-if="profilePlaceDetails.address_components?.length"
                                    class="mt-3 grid gap-3"
                                >
                                    <div
                                        v-for="component in profilePlaceDetails.address_components"
                                        :key="component.long_name + component.short_name"
                                        class="rounded-lg bg-muted/40 px-3 py-2 text-sm"
                                    >
                                        <p class="font-semibold">{{ component.long_name }}</p>
                                        <p class="text-xs text-muted-foreground">
                                            {{ component.short_name }} ·
                                            {{ component.types?.join(', ') || '—' }}
                                        </p>
                                    </div>
                                </div>
                                <p v-else class="mt-3 text-sm text-muted-foreground">
                                    No address components available.
                                </p>
                            </div>

                            <div class="grid gap-2 rounded-xl border p-4 text-sm">
                                <div class="flex justify-between">
                                    <span class="text-muted-foreground">UTC Offset</span>
                                    <span class="font-semibold">{{
                                        profilePlaceDetails.utc_offset ?? '—'
                                    }}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-muted-foreground">Vicinity</span>
                                    <span class="font-semibold text-right">{{
                                        profilePlaceDetails.vicinity || '—'
                                    }}</span>
                                </div>
                                <div class="flex items-center justify-between overflow-hidden">
                                    <span class="text-muted-foreground">Icon</span>
                                    <div class="flex items-center gap-2">
                                        <!-- <span class="font-mono text-xs">{{ profilePlaceDetails.icon || '—' }}</span>
                                  -->
                                        <img
                                            v-if="profilePlaceDetails.icon"
                                            :src="profilePlaceDetails.icon"
                                            alt="Place Icon"
                                            class="h-6 w-6 object-contain"
                                        />
                                        <span
                                            v-if="profilePlaceDetails.icon_background_color"
                                            class="inline-flex h-4 w-4 rounded-full border"
                                            :style="{
                                                backgroundColor:
                                                    profilePlaceDetails.icon_background_color
                                            }"
                                        ></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- </div> -->

                        <div v-else class="w-full text-sm text-muted-foreground">
                            No Google Place Details loaded.
                        </div>
                    </div>
                </div>
                <DrawerFooter>
                    <DrawerClose as-child>
                        <Button variant="outline"> Cancel </Button>
                    </DrawerClose>
                </DrawerFooter>
            </div>
        </DrawerContent>
    </Drawer>
</template>

<script setup lang="ts">
import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerDescription,
    DrawerFooter,
    DrawerClose
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useDepotProfileStore } from '@/stores/depot/profile.store';
import { storeToRefs } from 'pinia';

const profileStore = useDepotProfileStore();
const { drawerState, profilePlaceDetails } = storeToRefs(profileStore);

const onOpenChange = (val: boolean) => {
    profileStore.drawerState = val;

    if (!val) {
        // update the body element to ensure that the scroll is re-enabled
        document.body.style.overflow = 'auto';
        // clear the pointer-events  forom the body element
        document.body.style.pointerEvents = '';
    }
};
</script>
